import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showTransparent = isHomePage && !isScrolled;
  const textColor = showTransparent ? 'text-background-50' : 'text-foreground-900';
  const textMuted = showTransparent ? 'text-background-50/75' : 'text-foreground-700';
  const hoverBg = showTransparent ? 'hover:bg-background-50/15' : 'hover:bg-background-100';
  const logoBg = showTransparent ? 'bg-background-50/20' : 'bg-primary-500';
  const logoIcon = showTransparent ? 'text-background-50' : 'text-background-50';
  const borderColor = showTransparent ? 'border-background-50/20' : 'border-background-200/70';
  const dropdownBg = showTransparent ? 'bg-background-50/95 backdrop-blur-md' : 'bg-background-50';
  const primaryBtn = showTransparent
    ? 'bg-background-50 text-foreground-900 hover:bg-background-50/90'
    : 'bg-primary-500 text-background-50 hover:bg-primary-600';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
      }`}
    >
      <nav className="max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <div className={`w-9 h-9 rounded-lg ${logoBg} flex items-center justify-center`}>
              <i className={`ri-file-list-3-line text-lg ${logoIcon}`}></i>
            </div>
            <span className={`text-lg font-heading font-semibold ${textColor} whitespace-nowrap`}>
              BaseTao Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${textMuted} ${hoverBg} rounded-lg transition-colors cursor-pointer whitespace-nowrap`}
            >
              <i className="ri-home-4-line text-base"></i>
              <span>Home</span>
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${textMuted} ${hoverBg} rounded-lg transition-colors cursor-pointer whitespace-nowrap`}
              >
                <i className="ri-grid-line text-base"></i>
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className={`absolute top-full left-0 mt-1 w-64 ${dropdownBg} rounded-xl border ${borderColor} py-2 max-h-[420px] overflow-y-auto`}>
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/articles/basetao-spreadsheet-guide"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${textMuted} ${hoverBg} rounded-lg transition-colors cursor-pointer whitespace-nowrap`}
            >
              <i className="ri-book-open-line text-base"></i>
              <span>Shopping Guide</span>
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${textMuted} ${hoverBg} rounded-lg transition-colors cursor-pointer whitespace-nowrap`}
            >
              <i className="ri-external-link-line text-base"></i>
              <span>BaseTao Spreadsheet</span>
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap ${primaryBtn}`}
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg ${hoverBg} transition-colors cursor-pointer`}
          >
            <i className={`text-xl ${textColor} ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className={`md:hidden border-t ${borderColor} py-4 space-y-1 ${showTransparent ? 'bg-background-50/95 backdrop-blur-md' : 'bg-background-50'}`}>
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-home-4-line text-base text-primary-600"></i>
              </div>
              <span>Home</span>
            </Link>

            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-grid-line text-base text-secondary-600"></i>
                  </div>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isDropdownOpen && (
                <div className="pl-4 space-y-0.5 mt-1 max-h-[300px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 rounded-lg hover:bg-background-100 transition-colors"
                      onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); }}
                    >
                      <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/articles/basetao-spreadsheet-guide"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-book-open-line text-base text-accent-600"></i>
              </div>
              <span>Shopping Guide</span>
            </Link>

            <div className="border-t border-background-200/70 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-external-link-line text-base text-foreground-600"></i>
              </div>
              <span>BaseTao Spreadsheet</span>
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 mx-4 px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-shopping-bag-3-line"></i>
              <span>Buy It Now</span>
            </a>
          </div>
        )}
      </nav>
    </header>
  );
}